import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DashboardWidgets } from "@/components/analytics/DashboardWidgets";
import { JobAnalytics } from "@/components/analytics/JobAnalytics";
import { FormAnalytics } from "@/components/analytics/FormAnalytics";
import { WorkflowAnalytics } from "@/components/analytics/WorkflowAnalytics";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

export function OperationsTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeWorkflows: 0,
    formSubmissions: 0,
  });

  useEffect(() => {
    loadStats();
  }, [user]);

  const loadStats = async () => {
    if (!user) return;

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) return;

      const [jobsResult, workflowsResult, formsResult] = await Promise.all([
        supabase
          .from("jobs")
          .select("id", { count: "exact", head: true })
          .eq("org_id", profile.org_id),
        supabase
          .from("workflows")
          .select("id", { count: "exact", head: true })
          .eq("org_id", profile.org_id)
          .eq("is_active", true),
        supabase
          .from("form_submissions")
          .select("id", { count: "exact", head: true })
          .eq("org_id", profile.org_id),
      ]);

      setStats({
        totalJobs: jobsResult.count || 0,
        activeWorkflows: workflowsResult.count || 0,
        formSubmissions: formsResult.count || 0,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Operations Analytics</h2>
        <p className="text-muted-foreground">Job performance, forms, and workflow metrics</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-1">Total Jobs</p>
          <p className="text-3xl font-bold">{stats.totalJobs}</p>
        </Card>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-1">Active Workflows</p>
          <p className="text-3xl font-bold">{stats.activeWorkflows}</p>
        </Card>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-1">Form Submissions</p>
          <p className="text-3xl font-bold">{stats.formSubmissions}</p>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="jobs">Job Analytics</TabsTrigger>
          <TabsTrigger value="forms">Form Analytics</TabsTrigger>
          <TabsTrigger value="workflows">Workflow Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <DashboardWidgets />
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <JobAnalytics />
        </TabsContent>

        <TabsContent value="forms" className="mt-6">
          <FormAnalytics />
        </TabsContent>

        <TabsContent value="workflows" className="mt-6">
          <WorkflowAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
}
